/**
 * Role codes — the pure vocabulary every permission rule is written in.
 * Mirrors the backend's `Role` model (#1836, branch `new_roles`): three
 * company roles and the project roles of the producer/scriptwriter model,
 * each code belonging to exactly one scope. Nothing here touches React or the
 * network.
 *
 * @module access/roles
 */

import { userRole } from '@/enums';

/** Company-scope codes, in the order the pickers list them. */
export const COMPANY_ROLE_CODES = [userRole.company_owner, userRole.company_admin, userRole.company_employee] as const;

/** Project-scope codes the pickers offer. The scriptwriter and reviewer codes
 *  are retired (soft-deleted on the backend): still readable on old
 *  memberships, never offered. */
export const PROJECT_ROLE_CODES = [userRole.project_owner, userRole.project_admin, userRole.editor] as const;

/**
 * Company roles that apply to every project of the company («сквозные») —
 * the backend's `User::companyProjects()`. `GET user` reports those projects
 * with the company role and `company_effective`, and a holder is never given
 * a project role on top. A company *employee* is membership only: projects
 * come through project roles.
 */
export const COMPANY_WIDE_ROLE_CODES = [userRole.company_owner, userRole.company_admin] as const;

/**
 * Codes from before the rename, read as their new names. The dev backend
 * still answers them, and `new_roles`'s migration renames the company rows
 * but leaves `project_producer` as it was.
 */
const LEGACY_CODES: Readonly<Record<string, userRole>> = {
    company_producer: userRole.company_owner,
    admin: userRole.company_admin,
    employee: userRole.company_employee,
    project_producer: userRole.project_owner,
};

const ALL_CODES = new Set<string>(Object.values(userRole));
const COMPANY_CODES = new Set<string>(COMPANY_ROLE_CODES);

/** Narrows an arbitrary string (a wire `role_code`) to a known role code,
 *  renaming a legacy one. */
export function asRoleCode(code: string | null | undefined): userRole | null {
    if (code === null || code === undefined) return null;
    if (ALL_CODES.has(code)) return code as userRole;
    return LEGACY_CODES[code] ?? null;
}

export function isCompanyRole(code: userRole | string | null | undefined): boolean {
    const known = asRoleCode(code);
    return known !== null && COMPANY_CODES.has(known);
}

export function isProjectRole(code: userRole | string | null | undefined): boolean {
    const known = asRoleCode(code);
    return known !== null && !COMPANY_CODES.has(known);
}

export function isCompanyWideRole(code: userRole | string | null | undefined): boolean {
    const known = asRoleCode(code);
    return known !== null && (COMPANY_WIDE_ROLE_CODES as readonly userRole[]).includes(known);
}
